import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { BrandLockup, BrandMark } from '../../components/Brand'
import { api, type PublicPlan } from '../../lib/api'

const features = [
  ['01', 'Orçamentos sem retrabalho', 'Monte propostas consistentes com itens, componentes e cálculos centralizados.'],
  ['02', 'Operação em um só lugar', 'Acompanhe cliente, negociação, produção, instalação e entrega sem planilhas soltas.'],
  ['03', 'Margem sob controle', 'Enxergue custos e valores antes de fechar, com uma base comercial organizada.'],
  ['04', 'Equipe alinhada', 'Cada pessoa acessa a empresa certa, com papéis, permissões e limites claros.'],
  ['05', 'Histórico confiável', 'Versões, mudanças e decisões permanecem registradas para consulta.'],
  ['06', 'Pronto para crescer', 'Planos, cobrança recorrente e arquitetura SaaS acompanham a evolução da operação.'],
]

export function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false)
  const plans = useQuery({ queryKey: ['public-plans'], queryFn: api.publicPlans })
  const displayPlans = (plans.data?.items ?? []).flatMap(plan => {
    const price = plan.prices.find(item => item.billingInterval === 'month' && item.currency === 'BRL')
    return price ? [{ plan, price }] : []
  })

  return (
    <div className="marketing-page">
      <header className="site-header">
        <div className="site-container header-inner">
          <Link className="wordmark" to="/" aria-label="cromaOS — início"><BrandLockup /></Link>
          <button className="menu-button" type="button" aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'} aria-expanded={menuOpen} aria-controls="site-navigation" onClick={() => setMenuOpen(value => !value)}>
            <span /><span />
          </button>
          <nav id="site-navigation" className={menuOpen ? 'site-nav site-nav-open' : 'site-nav'} aria-label="Navegação principal">
            <a href="#produto" onClick={() => setMenuOpen(false)}>Produto</a>
            <a href="#processo" onClick={() => setMenuOpen(false)}>Como funciona</a>
            <a href="#planos" onClick={() => setMenuOpen(false)}>Planos</a>
            <a href="#duvidas" onClick={() => setMenuOpen(false)}>Dúvidas</a>
          </nav>
          <div className="header-actions">
            <Link className="text-link" to="/entrar">Entrar</Link>
            <Link className="button-link button-small" to="/cadastro">Começar grátis <span aria-hidden="true">↗</span></Link>
          </div>
        </div>
      </header>

      <main>
        <section className="hero-section">
          <div className="hero-glow" aria-hidden="true" />
          <div className="site-container hero-grid">
            <div className="hero-copy">
              <p className="marketing-eyebrow"><span /> Acesso antecipado para operações sob medida</p>
              <h1>Projetos que vendem.<br /><em>Operações que fluem.</em></h1>
              <p className="hero-kicker">O sistema operacional para negócios sob medida.</p>
              <p className="hero-lead">O cromaOS organiza o caminho entre o primeiro contato e a entrega — para sua equipe orçar melhor, reduzir ruído e crescer com margem.</p>
              <div className="hero-actions">
                <Link className="button-link button-hero" to="/cadastro">Começar 14 dias grátis <span aria-hidden="true">→</span></Link>
                <a className="ghost-link" href="#processo">Conhecer o fluxo <span aria-hidden="true">↓</span></a>
              </div>
              <p className="hero-note">Sem cartão agora · Configure sua empresa em poucos minutos</p>
            </div>

            <div className="product-stage" aria-label="Prévia do painel cromaOS">
              <div className="stage-orbit orbit-one" aria-hidden="true" />
              <div className="stage-orbit orbit-two" aria-hidden="true" />
              <div className="product-window">
                <div className="window-topbar">
                  <div className="mini-wordmark"><BrandMark /> <span className="mini-brand-name">croma<b>OS</b></span></div>
                  <div className="window-search">⌕ &nbsp; Buscar projeto</div>
                  <div className="avatar">LM</div>
                </div>
                <div className="window-body">
                  <aside className="mock-sidebar" aria-hidden="true">
                    <i className="active" /><i /><i /><i /><i />
                  </aside>
                  <div className="mock-content">
                    <div className="mock-heading"><div><small>ORÇAMENTO #0248</small><strong>Cozinha Aurora</strong></div><span>Em aprovação</span></div>
                    <div className="metric-row">
                      <div><small>Valor do projeto</small><strong>R$ 28.450</strong><b>+12% margem</b></div>
                      <div><small>Previsão</small><strong>18 set</strong><b>Dentro do prazo</b></div>
                      <div><small>Responsável</small><strong>Larissa M.</strong><b>Comercial</b></div>
                    </div>
                    <div className="mock-lower">
                      <div className="progress-card">
                        <div className="mock-card-title"><strong>Progresso do projeto</strong><span>68%</span></div>
                        <div className="progress-track"><span /></div>
                        <ol><li className="done">Briefing</li><li className="done">Orçamento</li><li className="current">Aprovação</li><li>Produção</li></ol>
                      </div>
                      <div className="margin-card"><small>MARGEM PREVISTA</small><strong>32,8%</strong><div className="bars"><i /><i /><i /><i /><i /></div><span>Saudável</span></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="floating-chip chip-top"><span>✓</span><div><small>Proposta enviada</small><strong>há 2 minutos</strong></div></div>
              <div className="floating-chip chip-bottom"><span>↑</span><div><small>Margem protegida</small><strong>+ R$ 3.420</strong></div></div>
            </div>
          </div>
          <div className="site-container audience-line"><span>Feito para</span><strong>Marcenarias</strong><i /><strong>Estúdios de interiores</strong><i /><strong>Projetos sob medida</strong></div>
        </section>

        <section className="problem-section" id="produto">
          <div className="site-container split-heading">
            <p className="section-index">01 / O PRODUTO</p>
            <div><h2>Menos improviso.<br />Mais <em>clareza operacional.</em></h2><p>Quando a informação fica espalhada, a margem some nos detalhes. O cromaOS cria uma fonte única para sua equipe trabalhar.</p></div>
          </div>
          <div className="site-container feature-grid">
            {features.map(([number, title, description]) => <article className="feature-card" key={number}><span>{number}</span><h3>{title}</h3><p>{description}</p></article>)}
          </div>
        </section>

        <section className="workflow-section" id="processo">
          <div className="site-container workflow-grid">
            <div className="workflow-copy"><p className="section-index light">02 / UM FLUXO CONTÍNUO</p><h2>Da oportunidade à entrega, sem perder o fio.</h2><p>Cada etapa alimenta a próxima. Sua equipe avança com contexto, e você acompanha o que precisa de atenção.</p><Link className="inline-cta" to="/cadastro">Testar o cromaOS <span>→</span></Link></div>
            <ol className="workflow-list">
              <li><span>01</span><div><strong>Capte e qualifique</strong><p>Organize contatos, necessidades e próximos passos.</p></div></li>
              <li><span>02</span><div><strong>Orce com segurança</strong><p>Transforme catálogo e custos em uma proposta clara.</p></div></li>
              <li><span>03</span><div><strong>Produza com contexto</strong><p>Leve o projeto aprovado para execução sem redigitação.</p></div></li>
              <li><span>04</span><div><strong>Entregue e aprenda</strong><p>Conclua o ciclo e use o histórico para decidir melhor.</p></div></li>
            </ol>
          </div>
        </section>

        <section className="pricing-section" id="planos">
          <div className="site-container pricing-heading">
            <div><p className="section-index">03 / PLANOS DE LANÇAMENTO</p><h2>Comece leve.<br /><em>Escale no seu ritmo.</em></h2></div>
            <p className="billing-period">Cobrança mensal</p>
          </div>
          <div className="site-container pricing-grid">
            {plans.isPending && <p className="pricing-state">Carregando planos…</p>}
            {plans.isError && <p className="pricing-state error">Não foi possível carregar os planos. Tente novamente.</p>}
            {displayPlans.map(({ plan, price }) => (
              <article className={plan.code === 'profissional' ? 'price-card featured' : 'price-card'} key={plan.id}>
                {plan.code === 'profissional' && <span className="popular-label">MAIS ESCOLHIDO</span>}
                <p className="plan-name">{plan.name}</p><p className="plan-description">{plan.description}</p>
                <div className="price"><sup>R$</sup><strong>{formatMoney(price.amountCents)}</strong><span>/mês</span></div>
                <Link className={plan.code === 'profissional' ? 'plan-button solid' : 'plan-button'} to={`/cadastro?plano=${plan.code}&preco=${price.id}`}>Escolher {plan.name} <span>→</span></Link>
                <ul>{planFeatures(plan).map(feature => <li key={feature}><span>✓</span>{feature}</li>)}</ul>
              </article>
            ))}
          </div>
          <p className="pricing-disclaimer">Valores de lançamento do acesso antecipado. Todos os planos começam com 14 dias de avaliação.</p>
        </section>

        <section className="faq-section" id="duvidas">
          <div className="site-container faq-grid">
            <div><p className="section-index">04 / DÚVIDAS</p><h2>Antes de começar.</h2><p>O essencial para experimentar o cromaOS com tranquilidade.</p></div>
            <div className="faq-list">
              <details open><summary>Preciso cadastrar cartão para testar?<span>+</span></summary><p>Não. Você pode criar a conta e usar os 14 dias de avaliação antes de escolher como continuar.</p></details>
              <details><summary>Posso mudar de plano depois?<span>+</span></summary><p>Sim. A estrutura foi pensada para sua operação começar em um plano e evoluir quando precisar.</p></details>
              <details><summary>O cromaOS serve apenas para marcenarias?<span>+</span></summary><p>Não. Ele atende negócios que vendem e executam projetos sob medida, incluindo estúdios de interiores e decoração.</p></details>
              <details><summary>Meus dados ficam separados de outras empresas?<span>+</span></summary><p>Sim. Cada organização possui isolamento próprio, permissões por usuário e camadas adicionais de proteção no banco.</p></details>
            </div>
          </div>
        </section>

        <section className="final-cta"><div className="site-container final-cta-inner"><p className="marketing-eyebrow"><span /> SUA OPERAÇÃO, MAIS CLARA</p><h2>O próximo projeto pode começar diferente.</h2><p>Crie sua conta e organize os primeiros passos com 14 dias de avaliação.</p><Link className="button-link button-light" to="/cadastro">Começar agora <span>→</span></Link></div></section>
      </main>

      <footer className="site-footer"><div className="site-container footer-inner"><Link className="wordmark footer-wordmark" to="/"><BrandLockup /></Link><p>O sistema operacional para negócios sob medida.</p><div><Link to="/legal/terms">Termos</Link><Link to="/legal/privacy">Privacidade</Link><Link to="/entrar">Entrar</Link></div><small>© {new Date().getFullYear()} cromaOS</small></div></footer>
    </div>
  )
}

function formatMoney(amountCents: number) {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amountCents / 100)
}

function planFeatures(plan: PublicPlan) {
  const capabilities = (plan.capabilities ?? []).filter(item => item.enabled)
  const members = capabilities.find(item => item.key === 'members.max')
  const quoteLimit = capabilities.find(item => item.key === 'quotes.monthly.max')?.limit
  const features = [members?.limit === 1 ? '1 usuário' : members?.limit ? `Até ${members.limit} usuários` : 'Usuários ilimitados']
  if (capabilities.some(item => item.key === 'catalog.items.max')) features.push('Contatos, catálogo e componentes')
  if (capabilities.some(item => item.key === 'quotes.monthly.max')) features.push(quoteLimit ? `Até ${quoteLimit} orçamentos por mês` : 'Orçamentos ilimitados')
  if (['quotes.pdf', 'finance.access', 'production.access'].every(key => capabilities.some(item => item.key === key))) features.push('PDF, financeiro e produção')
  return features
}
